import * as React from "react";
import * as Chart from 'devextreme-react/chart'
import * as PieChart from 'devextreme-react/pie-chart'
import * as TreeMap from 'devextreme-react/tree-map'
import BPMButton from "../components/Button";
import { Toolbar, Item } from "devextreme-react/toolbar";
import Box from "../components/Box";
import { FilterParams, FilterContextType, compareFilterCtxEqual, comparePlatformCtxEqual } from "./context/filter";
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { Colors } from "../utils/corlor";
import { LoadPanel } from "devextreme-react";
import { DonutLargeOutlined, EqualizerOutlined, PieChartOutlined, TimelineOutlined, ViewAgendaOutlined, GridOnOutlined, PictureAsPdfOutlined, BarChartOutlined } from "@material-ui/icons";
import { Query, QueryContextType } from "./context/query";
import dxDataGrid from 'devextreme/ui/data_grid';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';

export type ChartType = Chart.ICommonSeriesSettingsProps['type'] | 'pie' | 'doughnut' | 'tree'
export interface BPMChartProps<Params = any> {
    title: string
    subTitle?: string
    type?: ChartType
    allowedTypes?: ChartType[]
    horizontal?: boolean

    chartProps?: { [key: string]: any }
    series?: {
        name: string,
        valueField: string,
        argumentField: string,
        smallValuesGrouping?: { topCount: number, mode: 'topN' }
    }
    customizeToolTip?: (e: any, dataSource: DataSource) => any
    additionalParams?: Params
    onRequestLoad: (params: FilterParams & Params & { query: string }, abortSignal: AbortSignal) => Promise<{}>

    toolBarItems?: () => JSX.Element[]

    detailCharts?: (React.ComponentClass | React.FunctionComponent)[]
    isDetailView?: boolean

    visualRangeEnd?: number

    queryContext: Query
    doesNotSupportQuery?: boolean
    children: ((type: ChartType) => React.ReactNode | React.ReactNode[]) | React.ReactNode | React.ReactNode[]
}
interface BPMChartState {
    empty: boolean,
    loading: boolean,
    type?: ChartType,
    detailArgument?: any,
    currentDetailComponentIndex: number

    visualRange?: { startValue?: number, endValue?: number }
}
class IBPMChart<Params = any> extends React.Component<BPMChartProps<Params>, BPMChartState> {

    static contextType = FilterContextType

    previousContext: FilterParams;
    context: FilterParams

    private store: CustomStore;
    private dataSource: DataSource;
    private chart: Chart.Chart | PieChart.PieChart | TreeMap.TreeMap | null;
    private chartContainer: HTMLDivElement | null;
    private abortController = new AbortController()

    constructor(p: BPMChartProps) {
        super(p);
        this.state = {
            empty: false,
            loading: false,
            type: this.props.type,
            currentDetailComponentIndex: 0,
            visualRange: p.visualRangeEnd ? { startValue: undefined, endValue: undefined } : undefined
        }
        this.store = new CustomStore({
            load: () => {
                this.abortController.abort()
                this.abortController = new AbortController()
                let query = '[]'
                if (!this.props.doesNotSupportQuery) {
                    query = JSON.stringify(this.props.queryContext.query)
                }
                return this.props.onRequestLoad({ ...this.context, ...this.props.additionalParams, query: query } as FilterParams & Params & { query: string }, this.abortController.signal).then((data: Array<any>) => {
                    if (this.props.visualRangeEnd != undefined && this.props.series) {
                        this.setState({
                            visualRange: { startValue: data[0][this.props.series.argumentField], endValue: data[this.props.visualRangeEnd][this.props.series.argumentField] },
                            loading: false, empty: data.length == 0
                        })
                    } else
                        this.setState({ loading: false, empty: data.length == 0 })
                    return data
                })
            }
        });
        this.dataSource = new DataSource({ store: this.store })
    }

    componentDidUpdate(prevProps: BPMChartProps<Params>) {
        let didReload = false
        if (!compareFilterCtxEqual(this.previousContext, this.context)) {
            console.log(`${this.props.title} is loading data cause context is no equal`)
            this.loadData();
            if (!comparePlatformCtxEqual(this.previousContext, this.context)) {
                setTimeout(() => {
                    this.chart?.instance.render({ force: true })
                }, 0)
            }
            didReload = true
        } else if (Boolean(this.props.additionalParams) != Boolean(prevProps.additionalParams)) {
            console.log(`${this.props.title} is loading data cause adiitionalParams is no equal`)
            this.loadData()
            didReload = true
        } else if (this.props.additionalParams) {
            let simpleNotEqual = Object.keys(this.props.additionalParams).find(k => {
                if (prevProps.additionalParams) {
                    return prevProps.additionalParams[k] != this.props.additionalParams![k]
                } else {
                    return true
                }
            }) != undefined
            if (simpleNotEqual) {
                console.log(`${this.props.title} is loading data cause adiitionalParams is no equal (2)`)
                this.loadData()
                didReload = true
            }
        }
        const queryDifferent = this.props.queryContext.query !== prevProps.queryContext.query && !(this.props.queryContext.query || []).equals((prevProps.queryContext.query || []))
        if (this.props.queryContext && !this.props.doesNotSupportQuery && !didReload && queryDifferent) {
            console.log(`${this.props.title} is loading data cause queryContext is no equal (3)`)
            this.loadData()
        }
        this.previousContext = this.context;
    }

    componentDidMount() {
        this.previousContext = this.context;
    }

    componentWillUnmount() {
        this.abortController.abort()
    }

    loadData() {
        this.abortController.abort()
        this.dataSource.reload()
        this.chart!.instance.showLoadingIndicator()
        this.setState({ loading: true })
    }

    renderChartByType() {
        switch (this.state.type) {
            case 'doughnut':
            case 'pie':
                return this.renderPieChart();
            case 'tree':
                return this.renderTreeChart();
            default:
                return this.renderChart();
        }
    }

    getChartTypeIcon(type: ChartType) {
        switch (type) {
            case 'bar':
                return <EqualizerOutlined className={type == this.state.type ? 'active' : ''} />
            case 'stackedbar':
                return <BarChartOutlined className={type == this.state.type ? 'active' : ''} />
            case 'pie':
                return <PieChartOutlined className={type == this.state.type ? 'active' : ''} />
            case 'doughnut':
                return <DonutLargeOutlined className={type == this.state.type ? 'active' : ''} />
            case 'tree':
                return <ViewAgendaOutlined className={type == this.state.type ? 'active' : ''} />
            case 'spline':
                return <TimelineOutlined className={type == this.state.type ? 'active' : ''} />
        }
    }

    renderCustomLoadingPanelIfNeeded() {
        if (this.chartContainer) {
            return <LoadPanel
                showPane
                showIndicator
                visible={this.state.loading}
                shading
                position={{ of: this.chartContainer }}
                shadingColor="rgba(0,0,0,0.4)" />
        }
        return null
    }

    renderSelectChartType() {
        const items: JSX.Element[] = []
        if (this.props.allowedTypes) {
            this.props.allowedTypes.forEach((type) => {
                items.push(<Item location="after" key={type} cssClass='limit-width'
                    locateInMenu="never"
                    render={() => {
                        return <BPMButton icon={this.getChartTypeIcon(type)} onClick={() => {
                            this.setState({ type: type })
                        }} />
                    }} />)
            })
        }
        return items
    }

    isQueryActive() {
        return !this.props.doesNotSupportQuery && this.props.queryContext.query && this.props.queryContext.query.length > 0
    }

    renderPieChart() {
        const { chartProps, customizeToolTip, series } = this.props
        const { type } = this.state
        return <PieChart.PieChart
            ref={(ref) => this.chart = ref}
            dataSource={this.dataSource}
            redrawOnResize
            palette={Colors}
            innerRadius={type == 'doughnut' ? .6 : undefined}
            type={type as 'doughnut' | 'pie'}
            onPointClick={(e) => {
                /*if (this.props.detailCharts && this.props.detailCharts.length) {
                    this.setState({ detailArgument: e.target.argument })
                }*/
            }}
            onOptionChanged={(e) => {
                if (this.props.visualRangeEnd && e.fullName == "argumentAxis.visualRange") {
                    this.setState({ visualRange: e.value })
                }
            }}
            {...chartProps}>
            <PieChart.CommonSeriesSettings>

                {type == 'doughnut' && <PieChart.Label
                    visible={true} backgroundColor={'transparent'}
                    position='inside' customizeText={(arg) => {
                        return `${arg.valueText} (${(arg as any).percentText})`
                    }}>
                    <PieChart.Connector visible={false} />
                </PieChart.Label>}
            </PieChart.CommonSeriesSettings>

            <PieChart.Tooltip enabled customizeTooltip={(e: any) => {
                if (customizeToolTip) {
                    return customizeToolTip(e, this.dataSource)
                }
                if (this.state.type != 'tree') {
                    return { color: e.point._styles.selection.fill, text: `${e.argument}${e.seriesName && !e.seriesName.startsWith('Series') ? ` ${e.seriesName}` : ''}: ${e.value}` }
                } else if (series) {
                    let total = this.dataSource.items().reduce((p, item) => p + item.count, 0)
                    return { text: `${e.node.data.count} for${series ? (' ' + series.name) : ''} ${e.node.data[series.argumentField]} (${(100 / total * e.node.data.count).toFixed(1)}%)` }
                }
            }} arrowLength={10} paddingLeftRight={8} />
            {series && <PieChart.Series {...series} />}
            <PieChart.Legend visible />
            <PieChart.LoadingIndicator enabled={false} show={false} />

            {this.props.children}
        </PieChart.PieChart>
    }

    renderChart() {
        const { horizontal, chartProps, customizeToolTip, series } = this.props
        const { type, visualRange } = this.state

        return <Chart.Chart
            ref={(ref) => this.chart = ref}
            dataSource={this.dataSource}
            redrawOnResize
            palette={Colors}
            rotated={horizontal}
            onPointClick={(e) => {
                /*if (this.props.detailCharts && this.props.detailCharts.length) {
                    this.setState({ detailArgument: e.target.argument })
                }*/
            }}
            onOptionChanged={(e) => {
                if (this.props.visualRangeEnd && e.fullName == "argumentAxis.visualRange") {
                    this.setState({ visualRange: e.value })
                }
            }}
            {...chartProps}>
            <Chart.CommonSeriesSettings type={type as Chart.ICommonSeriesSettingsProps['type']}>
                {type == 'spline' && <Chart.Point size={0} hoverStyle={{ size: 14, border: { width: 5 }, color: '#000' }}
                    hoverMode='onlyPoint' />}
            </Chart.CommonSeriesSettings>
            <Chart.CommonAxisSettings visible={false} allowDecimals={false}> {/** maxValueMargin={0.1} */}
                <Chart.Tick visible={false} />
                <Chart.Grid color={'#2E2E2E'} visible={false} />
            </Chart.CommonAxisSettings>
            <Chart.Tooltip enabled customizeTooltip={(e: any) => {
                if (customizeToolTip) {
                    return customizeToolTip(e, this.dataSource)
                }
                if (this.state.type != 'tree') {
                    return { color: e.point._styles.selection.fill, text: `${e.argument}${e.seriesName && !e.seriesName.startsWith('Series') ? ` ${e.seriesName}` : ''}: ${e.value}` }
                } else if (series) {
                    let total = this.dataSource.items().reduce((p, item) => p + item.count, 0)
                    return { text: `${e.node.data.count} for${series ? (' ' + series.name) : ''} ${e.node.data[series.argumentField]} (${(100 / total * e.node.data.count).toFixed(1)}%)` }
                }
            }} arrowLength={10} paddingLeftRight={8} />
            {series && <Chart.Series {...series} />}
            <Chart.Legend visible />
            <Chart.LoadingIndicator enabled={false} show={false} />
            <Chart.ArgumentAxis visualRange={visualRange} visualRangeUpdateMode='keep'>
                <Chart.Label overlappingBehavior='stagger' />
            </Chart.ArgumentAxis>
            {this.props.visualRangeEnd != undefined && <Chart.ScrollBar visible={true} />}
            {this.props.visualRangeEnd != undefined && <Chart.ZoomAndPan argumentAxis="both" dragToZoom />}
            {typeof this.props.children === 'function' ? this.props.children(type) : this.props.children}
        </Chart.Chart>
    }

    renderTreeChart() {
        const { chartProps, customizeToolTip, series } = this.props
        return <TreeMap.TreeMap
            ref={(ref) => this.chart = ref}
            dataSource={this.dataSource}
            redrawOnResize
            valueField={series?.valueField} labelField={series?.argumentField}
            {...chartProps}>
            <TreeMap.Tooltip enabled customizeTooltip={(e: any) => {
                if (customizeToolTip) {
                    return customizeToolTip(e, this.dataSource)
                }
                if (this.state.type != 'tree') {
                    return { color: e.point._styles.selection.fill, text: `${e.argument}${e.seriesName && !e.seriesName.startsWith('Series') ? ` ${e.seriesName}` : ''}: ${e.value}` }
                } else if (series) {
                    let total = this.dataSource.items().reduce((p, item) => p + item.count, 0)
                    return { text: `${e.node.data.count} for${series ? (' ' + series.name) : ''} ${e.node.data[series.argumentField]} (${(100 / total * e.node.data.count).toFixed(1)}%)` }
                }
            }} arrowLength={10} paddingLeftRight={8} />

            <TreeMap.LoadingIndicator enabled={false} show={false} />
            {this.props.children}
        </TreeMap.TreeMap>
    }

    render() {
        const { title, subTitle, toolBarItems, isDetailView } = this.props
        let DetailComp: undefined | React.ComponentClass<any> | React.FunctionComponent<any> = undefined
        if (this.state.detailArgument && this.props.detailCharts) {
            DetailComp = this.props.detailCharts[this.state.currentDetailComponentIndex]
        }
        return <Box noBox={isDetailView} borderColor={this.isQueryActive() ? 'var(--platform)' : undefined}>
            <div className='bpm-chart-container'>
                {!isDetailView && <Toolbar>
                    <Item location="before"
                        locateInMenu="never"
                        render={() => {
                            return <>
                                <div className='title'>{title}</div>
                                {subTitle && <span className='sub-title'>{subTitle}</span>}
                            </>
                        }} />
                    {toolBarItems && toolBarItems()}
                    {this.renderSelectChartType()}
                    <Item location="after"
                        locateInMenu="never"
                        render={() => {
                            return <BPMButton style={{ marginLeft: 6 }} className='export' icon={<PictureAsPdfOutlined style={{ fill: '#2a74ff' }} />} onClick={() => {
                                this.chart && this.chart.instance.exportTo('chart_data.pdf', 'PDF')
                            }} />
                        }} />
                    <Item location="after"
                        locateInMenu="never"
                        render={() => {
                            return <BPMButton className='export' icon={<GridOnOutlined style={{ fill: '#2a74ff' }} />} onClick={() => {
                                const div = document.createElement('div')
                                document.body.appendChild(div)
                                div.setAttribute('style', 'position: absolute; top: -10000px; left: -1000px;max-height: 10px')
                                console.log(this.dataSource)
                                let ds = this.dataSource.items()
                                if (this.state.type === 'tree' && this.dataSource.items().length && Array.isArray(this.dataSource.items()[0].items)) {
                                    ds = (ds as any).flatMap((p) => {
                                        return p.items
                                    })
                                }
                                const self = this
                                const dg = new dxDataGrid(div, {
                                    export: {
                                        enabled: true,
                                    },
                                    paging: {
                                        enabled: false
                                    },
                                    onContentReady: function (e) {
                                        const workbook = new Workbook();
                                        const worksheet = workbook.addWorksheet('Export');
                                        exportDataGrid({
                                            component: e.component!,
                                            worksheet: worksheet,
                                        }).then(() => {
                                            workbook.xlsx.writeBuffer()
                                                .then(function (buffer) {
                                                    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${self.props.title}.xlsx`);
                                                    dg.dispose()
                                                    document.body.removeChild(div)
                                                });
                                        })
                                    },
                                    dataSource: ds
                                })


                            }} />
                        }} />
                </Toolbar>}
                {DetailComp && <DetailComp  {...this.props} isDetailView />}
                {!DetailComp && <div className='bpm-chart' ref={(ref) => {
                    this.chartContainer = ref
                }}>
                    {this.renderCustomLoadingPanelIfNeeded()}
                    {this.renderChartByType()}
                    <span className='chart-empty-text' style={{ display: (this.state.empty && !this.state.loading) ? 'block' : 'none' }}>No Data Available</span>
                </div>}
            </div>
        </Box>
    }
}

export class BPMChart<Params = any> extends React.Component<Omit<BPMChartProps<Params>, 'queryContext'>, BPMChartState> {
    static contextType = QueryContextType
    context!: Query
    render() {
        return <IBPMChart<Params> {...this.props} queryContext={this.context} />
    }
}


// WEBPACK FOOTER //
// ./src/components/Chart.tsx